import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { planOf, planOfShop } from '../constants/plans';
import { useRefreshable } from '../context/RefreshContext';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import { fetchDesigners } from '../firebase/designerService';
import { fetchShops } from '../firebase/shopService';
import { weightedShuffle } from '../firebase/likeService';
import { ITEM_KINDS, imageCountOf } from '../constants/gallery';
// The same list the shop search filters by, so a category means one thing
// across the app. constants/gallery already validates a listing's `type`
// against it, which is why filtering on equality here is safe.
import { PRODUCT_TYPES } from '../constants/productTaxonomy';
import { currencySymbolOf } from '../constants/designer';
import GalleryLightbox from './GalleryLightbox';
import { SocialStats } from './Social';
import { fetchStats, fetchFeedWork, SOCIAL_KINDS } from '../firebase/socialService';
import '../pages/SearchPage.css';
import '../pages/MyDesigners.css';
import PersonIcon from './PersonIcon';

const FEED_DESIGNERS = 8;
const FEED_SHOPS = 8;
const FEED_PHOTOS = 12;

// ── RECOMMENDATIONS FEED ─────────────────────────────────────────────────────
// Designers, shops, then the work both of them publish — each ordered by a
// weighted random draw.
//
// Extracted from the My Designers page so the dashboard can carry it too —
// it belongs where people land, not one click away. Same component in both
// places, so the two can't drift.
//
// ORDERING is weighted-random rather than a leaderboard (see weightedShuffle):
// a straight sort by likes would show everyone the same handful forever and a
// new designer would never surface at all, while pure random throws away the
// quality signal. Weighting favours popular work but leaves every designer a
// real chance, and reshuffles on each visit.
// `split` — render PROJECTS and PRODUCTS as two capped blocks instead of one
// feed behind a filter. The dashboard keeps the filter: a signed-in reader is
// browsing and a toggle is cheap. The landing takes the split, because a
// visitor who has not signed up yet is being shown what the place contains,
// and "here are projects, here are products" says that without asking them to
// operate anything first.
//
// `perBlock` caps each block. Same component either way — the fetching, the
// weighted draw, the owner resolution and the lightbox are identical, and
// forking it for the landing is how the two would drift apart.
// `showWork` — render the grid of published projects and products. The
// dashboard wants it: a signed-in reader is there to browse. The landing does
// not: the two rails already say who is here, and a wall of thumbnails under
// them pushed the one thing that page is for — signing up — off the screen.
export default function RecommendationsFeed({ split = false, perBlock = 10, showWork = true }) {
  const { user } = useAuth();
  const { t } = useT();

  const [designers, setDesigners] = useState([]);
  const [shops, setShops] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxAt, setLightboxAt] = useState(-1);
  // Ids whose thumbnail failed to decode. A stored data URL can be truncated or
  // corrupt (an interrupted upload, an older encoder), and a broken-image icon
  // in a curated feed looks worse than one fewer photo — so those tiles are
  // dropped rather than rendered empty.
  const [broken, setBroken] = useState(() => new Set());
  // Static like/view/comment counts for the 3D tiles, keyed by projectId.
  // Fetched ONCE in a single batched query for the whole grid — deliberately
  // not a listener per tile, which is what makes a feed expensive.
  const [stats, setStats] = useState({});

  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const load = useCallback(async ({ force = false } = {}) => {
    try {
      // BOTH forced, not just the designers. The work pool is cached at module
      // level for five minutes, so a refresh that only forced `fetchDesigners`
      // re-rendered the same photos and designs it already had — the gesture
      // ran, the spinner turned, and nothing new appeared.
      // Three parallel reads, all cached at module level — the shop directory
      // is the same shape and the same cost as the designer one, and it is
      // needed here whether or not the shop rail has anything to show: without
      // it a shop's photo in the work grid has no byline.
      const [all, allShops, work] = await Promise.all([
        fetchDesigners({ force }),
        fetchShops({ force }),
        fetchFeedWork({ force }),
      ]);
      if (!alive.current) return;
      setDesigners(all);
      setShops(allShops);
      setPhotos(work);
    } finally {
      if (alive.current) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Pull to refresh. Forced past the five-minute directory cache, and it
  // reshuffles the feed as a side effect — new data means new weights, which
  // is what makes a refreshed feed feel like it refreshed.
  useRefreshable(() => load({ force: true }));

  // Every account that can publish, flattened to the four things a byline and
  // the shuffle weight actually need. Built from BOTH directories so a shop's
  // photo carries its shop's name and links to its shop page — before this the
  // map held designers only, and a product in the grid appeared anonymous.
  const byUid = useMemo(() => {
    const m = new Map();
    for (const d of designers) {
      const id = d.uid || d.id;
      m.set(id, {
        name: d.fullName, avatarUrl: d.avatarUrl, likeCount: d.likeCount || 0,
        path: `/designer/${id}`, isShop: false,
      });
    }
    for (const s of shops) {
      const id = s.uid || s.id;
      m.set(id, {
        name: s.name, avatarUrl: s.avatarUrl, likeCount: s.likeCount || 0,
        path: `/shop/${id}`, isShop: true,
      });
    }
    return m;
  }, [designers, shops]);

  // Memoised on the DATA, not on render — reshuffling every render would make
  // the feed jump under the cursor.
  // ── PLAN FIRST, THEN CHANCE ────────────────────────────────────────────────
  // Gold designers lead, then silver, then bronze, then free — and WITHIN each
  // tier the order is reshuffled on every visit, so nobody who pays the same
  // amount is permanently ahead of anyone else who does.
  //
  // Expressed as "group by tier, shuffle each group, concatenate high to low"
  // rather than as a comparator. A comparator returning a random number for
  // equal ranks is not a valid ordering — it is inconsistent between the same
  // pair on different comparisons, which lets a sort produce a biased or even
  // corrupted result depending on the engine's algorithm. Shuffling each bucket
  // once and joining them is uniform by construction.
  //
  // EMPTY TIERS COST NOTHING: a tier with no designers contributes an empty
  // array, so if nobody is gold the list simply starts at silver. No special
  // case is needed for "if nobody with gold plan".
  //
  // NOTE — this replaces the like-weighted draw for THIS rail. Ranking is now
  // what the plan is for; likes still order the shops rail and the work grid
  // below, which no plan applies to.
  const feedDesigners = useMemo(() => {
    // ── PAID TIERS ONLY ──────────────────────────────────────────────────────
    // Free designers are excluded from this rail entirely. Being recommended is
    // part of what a plan buys, so the rail is a placement rather than a
    // directory — the full list, free accounts included, is one tap away at
    // /designers and stays complete there.
    //
    // A FILTER, unlike the ordering below. Everywhere else plan only RANKS, so
    // a free designer still appears; here they are removed. That is the whole
    // difference between the rail and search, and it is deliberate.
    const pool = designers.filter(d => (d.uid || d.id) !== user?.uid
      && planOf(d).searchRank > 0);

    // Fisher-Yates on a copy — never sorts the cached directory in place.
    const shuffled = (arr) => {
      const a = [...arr];
      for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
      }
      return a;
    };

    const byTier = new Map();
    for (const d of pool) {
      const rank = planOf(d).searchRank;
      if (!byTier.has(rank)) byTier.set(rank, []);
      byTier.get(rank).push(d);
    }

    return [...byTier.keys()]
      .sort((a, b) => b - a)                 // highest tier first
      .flatMap(rank => shuffled(byTier.get(rank)))
      .slice(0, FEED_DESIGNERS);
  }, [designers, user?.uid]);

  // ── RECOMMENDED SHOPS IS SOLD, LIKE THE DESIGNER RAIL ──────────────────────
  // free  'none'   — never here. Being recommended is part of what a plan buys.
  // silver 'chance' — competes for whatever places gold has not taken.
  // gold  'always'  — always here, and ahead of silver.
  //
  // 'chance' IS THE ORDERING, not a dice roll. Gold shops fill the rail first
  // and silver takes what is left, so a silver shop appears whenever there is
  // room and never displaces a gold one. A random coin flip could hide a shop
  // that paid for a guaranteed place, which is the one thing this must not do.
  //
  // Grouped and shuffled rather than sorted with a random comparator — see the
  // designer rail above for why that is not a valid ordering.
  const feedShops = useMemo(() => {
    const pool = shops.filter(s => (s.uid || s.id) !== user?.uid
      && planOfShop(s).recommended !== 'none');

    const shuffled = (arr) => {
      const a = [...arr];
      for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
      }
      return a;
    };

    const byTier = new Map();
    for (const sh of pool) {
      const rank = planOfShop(sh).searchRank;
      if (!byTier.has(rank)) byTier.set(rank, []);
      byTier.get(rank).push(sh);
    }

    return [...byTier.keys()]
      .sort((a, b) => b - a)
      .flatMap(rank => shuffled(byTier.get(rank)))
      .slice(0, FEED_SHOPS);
  }, [shops, user?.uid]);

  // ── WHAT KIND OF WORK ────────────────────────────────────────────────────
  // The grid holds two different things now — a designer's work and a shop's
  // products — and they are looked for at different times. WHO PUBLISHED IT is
  // what tells them apart, not the item: a product is a photo like any other,
  // and only its owner's role makes it a listing (see constants/gallery.js).
  //
  // An owner in neither directory counts as a project, not a product. That is
  // the safe fall: an unresolved owner should not silently populate a shopping
  // list, and it is the same row that already renders without a byline.
  const [workFilter, setWorkFilter] = useState('all');
  // ── AND, FOR PRODUCTS, WHICH KIND ─────────────────────────────────────────
  // '' means every kind, which is what an untouched dropdown says and what the
  // feed showed before this existed. Only consulted while `workFilter` is
  // 'products' — a category means nothing for a designer's project.
  //
  // NOT cleared when the tab changes. Someone who narrows to Lighting, looks at
  // Projects and comes back expects Lighting to still be there; the control is
  // visible whenever it applies, so a live filter can never be hidden.
  const [productType, setProductType] = useState('');
  const isProduct = useCallback(
    (p) => !!byUid.get(p.ownerUid)?.isShop,
    [byUid],
  );

  // Applied BEFORE the draw, so a filtered view fills its twelve slots from the
  // whole pool rather than showing whatever survived a draw made across both.
  const pool = useMemo(() => {
    const mine = photos.filter(p => p.ownerUid !== user?.uid);
    if (workFilter === 'products') {
      const all = mine.filter(isProduct);
      // An unset category is "everything", not "nothing" — so the whole
      // product pool falls through untouched.
      return productType ? all.filter(p => p.type === productType) : all;
    }
    if (workFilter === 'projects') return mine.filter(p => !isProduct(p));
    return mine;
  }, [photos, user?.uid, workFilter, isProduct, productType]);

  // A photo has no likes of its own, so it inherits its owner's standing —
  // whichever kind of account that owner is.
  const feedPhotos = useMemo(
    () => weightedShuffle(
      pool,
      p => byUid.get(p.ownerUid)?.likeCount || 0,
    ).slice(0, FEED_PHOTOS),
    [pool, byUid],
  );

  // ── SPLIT MODE ────────────────────────────────────────────────────────────
  // Two independent draws rather than one draw then a partition: partitioning
  // would let a lopsided pool fill one block and leave the other nearly empty,
  // which is exactly what happens early on when there are many projects and
  // few products. Each block draws from its own pool and fills its own cap.
  //
  // Computed unconditionally — hooks cannot be called behind a condition — and
  // simply unused when `split` is false.
  const mineOnly = useMemo(
    () => photos.filter(p => p.ownerUid !== user?.uid),
    [photos, user?.uid],
  );
  const splitProjects = useMemo(
    () => weightedShuffle(
      mineOnly.filter(p => !isProduct(p)),
      p => byUid.get(p.ownerUid)?.likeCount || 0,
    ).slice(0, perBlock),
    [mineOnly, isProduct, byUid, perBlock],
  );
  const splitProducts = useMemo(
    () => weightedShuffle(
      mineOnly.filter(isProduct),
      p => byUid.get(p.ownerUid)?.likeCount || 0,
    ).slice(0, perBlock),
    [mineOnly, isProduct, byUid, perBlock],
  );

  // Whether the SECTION appears at all is decided by the unfiltered pool. Tied
  // to the filtered one, picking a filter with no matches would take the
  // buttons off screen along with the results — leaving no way back.
  const hasAnyWork = useMemo(
    () => photos.some(p => p.ownerUid !== user?.uid),
    [photos, user?.uid],
  );

  // The lightbox pages through exactly what is on screen, so it walks the
  // FILTERED list — indexing into the unfiltered one would skip or repeat
  // whenever a broken thumbnail was dropped.
  const shown = feedPhotos.filter(p => !broken.has(p.id));
  // The split blocks drop broken thumbnails for the same reason — see `broken`.
  const splitProjectsShown = splitProjects.filter(p => !broken.has(p.id));
  const splitProductsShown = splitProducts.filter(p => !broken.has(p.id));

  // EVERYTHING, photos and designs together. Designs used to be filtered out
  // because they opened their own tab; now the lightbox reads each item's own
  // kind, so one mixed list is right and the indices line up with the grid.
  // WHAT THE LIGHTBOX PAGES THROUGH, and it must be exactly what is on screen.
  // `openItem` finds a tile by `indexOf`, so in split mode this cannot stay as
  // the single feed's list: a tile from either block would not be found, the
  // index would come back -1, and clicking a photo on the landing page would
  // do nothing at all. Concatenated in render order, so paging past the last
  // project continues into the products.
  const lightboxItems = split
    ? [...splitProjectsShown, ...splitProductsShown]
    : shown;

  // Counts for whatever 3D tiles ended up on screen. Runs after the shuffle
  // settles, so it asks for exactly the projects being rendered and no more.
  // `join` as the dependency: `shown` is a new array on every render and would
  // otherwise re-fetch forever.
  const projectIdKey = shown
    .filter(p => p.kind === ITEM_KINDS.DESIGN && p.projectId)
    .map(p => p.projectId).join(',');
  const photoIdKey = shown
    .filter(p => p.kind === ITEM_KINDS.PHOTO)
    .map(p => p.id).join(',');

  useEffect(() => {
    if (!projectIdKey) return undefined;
    let live = true;
    fetchStats(SOCIAL_KINDS.PROJECT, projectIdKey.split(','))
      .then(s => { if (live) setStats(prev => ({ ...prev, ...s })); })
      .catch(() => {});
    return () => { live = false; };
  }, [projectIdKey]);

  // Photos are a second batched query — a different collection, so it cannot
  // share the projects one. Still two queries for the whole grid, not two per
  // tile. Keyed by the PHOTO's own id, which cannot collide with a projectId.
  useEffect(() => {
    if (!photoIdKey) return undefined;
    let live = true;
    fetchStats(SOCIAL_KINDS.PHOTO, photoIdKey.split(','))
      .then(s => { if (live) setStats(prev => ({ ...prev, ...s })); })
      .catch(() => {});
    return () => { live = false; };
  }, [photoIdKey]);

  // Both stats effects key on the id LISTS, so they do not re-run when a
  // refresh brings the same tiles back with different counts — which is the
  // whole point of refreshing a feed. Re-read explicitly.
  useRefreshable(async () => {
    const jobs = [];
    if (projectIdKey) jobs.push(fetchStats(SOCIAL_KINDS.PROJECT, projectIdKey.split(',')));
    if (photoIdKey) jobs.push(fetchStats(SOCIAL_KINDS.PHOTO, photoIdKey.split(',')));
    if (!jobs.length) return;
    const results = await Promise.allSettled(jobs);
    if (!alive.current) return;
    const merged = results.reduce(
      (acc, r) => (r.status === 'fulfilled' ? { ...acc, ...r.value } : acc), {});
    setStats(prev => ({ ...prev, ...merged }));
  });


  // Keeps the STATICALLY fetched counts honest after a like made in the
  // lightbox. Cheaper and calmer than re-querying: the delta is already known,
  // and the next real fetch brings the authoritative number anyway.
  const bumpLikes = (id, delta) => setStats(prev => (prev[id] ? {
    ...prev,
    [id]: { ...prev[id], likesCount: Math.max(0, (prev[id].likesCount || 0) + delta) },
  } : prev));

  // EVERYTHING OPENS IN THE LIGHTBOX, designs included.
  //
  // A 3D design used to open the read-only /share walkthrough in a new tab.
  // That tab carries no like button, no view count and no comments, so a
  // design was the one thing in the feed you could not react to — and getting
  // back meant closing a tab rather than closing an overlay. It now behaves
  // like a photo: its preview opens here with the same bar and the same
  // thread, and the walkthrough is a link from inside.
  function openItem(p) {
    const at = lightboxItems.indexOf(p);
    if (at >= 0) setLightboxAt(at);
  }

  if (loading) return null;
  if (!feedDesigners.length && !feedShops.length && !(showWork && hasAnyWork)) return null;


  // ── ONE TILE ──────────────────────────────────────────────────────────────
  // Extracted so the single-feed and split layouts render the SAME markup.
  // Two copies of seventy lines of JSX is how a byline stops appearing in one
  // of them and nobody notices for a month.
  const renderTile = (p) => {
              const owner = byUid.get(p.ownerUid);
              const is3d = p.kind === ITEM_KINDS.DESIGN;
              return (
                <div key={p.id} className="md-photo">
                  {owner && (
                    // ABOVE THE IMAGE, the way every feed presents a post:
                    // you see whose work it is before you see the work.
                    // Underneath it read as a caption on the previous photo.
                    <Link to={owner.path} className="md-photo-by">
                      <span className="md-photo-avatar">
                        {owner.avatarUrl
                          ? <img src={owner.avatarUrl} alt="" />
                          : <PersonIcon />}
                      </span>
                      {owner.name}
                    </Link>
                  )}
                  <button
                    type="button"
                    className={`md-photo-btn${is3d ? ' md-photo-btn--3d' : ''}`}
                    onClick={() => openItem(p)}
                    title={is3d ? t('dg_tab_3d') : t('dg_tab_img')}
                  >
                    <img
                      src={p.thumbUrl}
                      alt={p.description || ''}
                      loading="lazy"
                      onError={() => setBroken(prev => new Set(prev).add(p.id))}
                    />
                    {/* Which kind this is, before you click. A 3D design opens a
                        new tab and a photo opens a lightbox — two very different
                        outcomes that otherwise look identical in the grid. */}
                    <span className={`md-kind${is3d ? ' md-kind--3d' : ''}`} aria-hidden="true">
                      {/* HOW MANY PICTURES, when there is more than one. The
                          badge already occupies this corner saying what kind of
                          item it is; for a multi-image post the count is the
                          more useful thing it can say, and it implies the kind
                          anyway — a 3D design is never a carousel. */}
                      {!is3d && imageCountOf(p) > 1 ? `1/${imageCountOf(p)}` : is3d ? '3D' : (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                          stroke="currentColor" strokeWidth="2.2"
                          strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="3" width="18" height="18" rx="2" />
                          <circle cx="8.5" cy="8.5" r="1.6" />
                          <path d="M21 15l-5-5L5 21" />
                        </svg>
                      )}
                    </span>
                    {/* A shop's product carries its PRICE at the top and its
                        NAME along the bottom — opposite corners, so neither
                        crowds the other and the picture keeps its middle.
                        Both are always visible: a product grid is scanned for
                        exactly these two, and hiding either behind a hover
                        leaves a phone with tiles that say nothing.

                        Nothing renders for a photo without a price, which is
                        every photo a designer has published. */}
                    {p.price != null && (
                      <>
                        <span className="md-price">
                          {p.price} {currencySymbolOf(p.currency)}
                        </span>
                        {p.title && <span className="md-name">{p.title}</span>}
                      </>
                    )}
                  </button>
                  {/* Static counts. No listener, no per-tile read — these came
                      from one batched query for the whole grid. */}
                  {stats[is3d ? p.projectId : p.id] && (
                    <SocialStats stats={stats[is3d ? p.projectId : p.id]} className="md-photo-stats" />
                  )}
                </div>
    );
  };

  return (
    <>
      {feedDesigners.length > 0 && (
        <section className="md-section">
          <h2 className="md-heading">{t('md_recommended')}</h2>
          <p className="md-sub">{t('md_recommended_sub')}</p>
          {/* A CAROUSEL, not a grid. Eight designers stacked as full-width
              rows pushed the published work — the reason people scroll this
              page — off the bottom of a phone. One scrollable row shows the
              same eight in the height of a single card, and horizontal
              overflow is itself the affordance: a half-visible card at the
              edge says "there is more this way" without a control. */}
          <div className="rec-rail">
            {feedDesigners.map(d => (
              // The card carries the designer's plan, so a paid tier is visible
              // in the rail rather than only on the profile. `planOf` falls back
              // to free for a missing or unknown value, so this can never
              // produce a class with no rule behind it. Shops below keep the
              // plain card — the designer ladder is not sold to them.
              <Link key={d.uid || d.id} to={`/designer/${d.uid || d.id}`} className={`rec-card rec-card--${planOf(d).id}`}>
                <span className="rec-avatar">
                  {d.avatarUrl ? <img src={d.avatarUrl} alt="" /> : <PersonIcon />}
                </span>
                {/* Experience above the name, as asked. Location and the like
                    count are dropped — at this size they turn the card into a
                    paragraph, and both are one tap away on the profile. */}
                {d.experienceYears != null && d.experienceYears !== '' && (
                  <span className="rec-exp">{t('ds_years_short').replace('{n}', d.experienceYears)}</span>
                )}
                <span className="rec-name">{d.fullName}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ── SHOPS ── its own rail, below the designers.
          A separate section rather than mixed into the one above: the two
          answer different questions ("who could design this" vs "where do I
          buy it"), and interleaving them would make a rail you cannot scan for
          either. Same markup and the same `rec-*` classes, so the two rails
          scroll and size identically without a second stylesheet. */}
      {feedShops.length > 0 && (
        <section className="md-section">
          <h2 className="md-heading">{t('md_shops')}</h2>
          <p className="md-sub">{t('md_shops_sub')}</p>
          <div className="rec-rail">
            {feedShops.map(s => {
              const id = s.uid || s.id;
              return (
                <Link
                  key={id}
                  /* A shop wears its own tier, from the shop ladder. */
                  to={`/shop/${id}`}
                  className={`rec-card rec-card--${planOfShop(s).id}`}
                >
                  <span className="rec-avatar">
                    {s.avatarUrl ? <img src={s.avatarUrl} alt="" /> : <PersonIcon />}
                  </span>
                  {/* What they sell, in the slot the designer card gives to
                      experience — the one fact worth carrying at this size.
                      Only the first category: the card is a name and a hint,
                      and the full list is one tap away. */}
                  {s.categories?.length > 0 && (
                    <span className="rec-exp">{t(`shop_cat_${s.categories[0]}`)}</span>
                  )}
                  <span className="rec-name">{s.name}</span>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {showWork && hasAnyWork && (
        <section className="md-section">
          <h2 className="md-heading">{t('md_work')}</h2>
          <p className="md-sub">{t('md_projects_sub')}</p>

          {/* ── ALL · PROJECTS · PRODUCTS ──
              The same segmented control the search page uses, reusing its
              `sp-mode` classes — SearchPage.css is already imported here, and
              two switches that do the same job should not look different.

              Always all three, even when one is empty: a control that appears
              and disappears with the data is one you cannot learn. Picking an
              empty one says so in words below. */}
          {!split && (
            <div className="sp-modes md-filters" role="tablist" aria-label={t('md_work')}>
              {[
                { key: 'all',      label: t('md_filter_all') },
                { key: 'projects', label: t('md_filter_projects') },
                { key: 'products', label: t('md_filter_products') },
              ].map(f => (
                <button
                  key={f.key}
                  type="button"
                  role="tab"
                  aria-selected={workFilter === f.key}
                  className={`sp-mode${workFilter === f.key ? ' sp-mode--on' : ''}`}
                  onClick={() => setWorkFilter(f.key)}
                >{f.label}</button>
              ))}
            </div>
          )}

          {/* ── WHICH KIND OF PRODUCT ────────────────────────────────────────
              Under the tabs, and only while Products is the active one: a
              category applies to a shop's listing and means nothing for a
              designer's project, so showing it on the other tabs would offer a
              control that cannot do anything.

              The same options and the same `shop_cat_*` labels the shop search
              uses (see SearchPage), so "Lighting" means one thing across the
              app. "Any type" is the default and is a real choice — picking it
              again is how you widen back out. */}
          {!split && workFilter === 'products' && (
            <label className="md-ptype">
              <span className="md-ptype-label">{t('prod_type')}</span>
              <select
                className="sp-select md-ptype-select"
                value={productType}
                onChange={e => setProductType(e.target.value)}
              >
                <option value="">{t('search_any')}</option>
                {PRODUCT_TYPES.map(k => (
                  <option key={k} value={k}>{t(`shop_cat_${k}`)}</option>
                ))}
              </select>
            </label>
          )}

          {split ? (
            // TWO BLOCKS, each with its own heading and its own cap. A block
            // with nothing in it is omitted rather than shown empty: on a
            // landing page an empty grid under a confident heading reads as
            // something being broken.
            <>
              {splitProjectsShown.length > 0 && (
                <>
                  <h3 className="md-block-h">{t('md_filter_projects')}</h3>
                  <div className="md-photos">{splitProjectsShown.map(renderTile)}</div>
                </>
              )}
              {splitProductsShown.length > 0 && (
                <>
                  <h3 className="md-block-h">{t('md_filter_products')}</h3>
                  <div className="md-photos">{splitProductsShown.map(renderTile)}</div>
                </>
              )}
            </>
          ) : feedPhotos.length === 0 ? (
            <p className="sp-empty">
              {workFilter === 'products' ? t('md_no_products') : t('md_no_projects')}
            </p>
          ) : (
            <div className="md-photos">
              {shown.map(renderTile)}
            </div>
          )}
        </section>
      )}

      {lightboxAt >= 0 && lightboxItems[lightboxAt] && (
        <GalleryLightbox
          item={lightboxItems[lightboxAt]}
          kind={ITEM_KINDS.PHOTO}
          onClose={() => setLightboxAt(-1)}
          onLikeChange={bumpLikes}
        />
      )}
    </>
  );
}
